import React, {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {Col, Label, Row} from 'reactstrap';
import {AvCheckbox, AvCheckboxGroup, AvForm, AvGroup, AvInput} from 'availity-reactstrap-validation';
import {IRootState} from 'app/shared/reducers';
import {setRegistrationData} from 'app/entities/registration/registration.reducer';
import {getSymptomsByEmergency} from 'app/entities/symptom/symptom.reducer';
import 'app/entities/registration/registration.scss';
import {CaseType} from 'app/shared/model/enumerations/case-type.model';
import RegistrationButtons from './registration-buttons';

interface IMotherSymptomsProps extends StateProps, DispatchProps {
  handlePreviousStep: () => void;
  handleNextStep: () => void;
  currentStep: number;
  onFinish?: () => void;
  updating?: boolean;
}

const MotherSymptoms: React.FC<IMotherSymptomsProps> = (props: IMotherSymptomsProps) => {
  const { countryId, symptoms, loading, patientCase, registrationData} = props;
  const [selectedSymptoms, setSelectedSymptoms] = useState<string[]>([]);
  const [motherSymptom, setMotherSymptom] = useState(patientCase?.motherSymptom || '');
  const [isOtherSelected, setIsOtherSelected] = useState(false);

  useEffect(() => {
    if (patientCase?.emergencyType && countryId) {
      props.getSymptomsByEmergency(patientCase?.emergencyType, countryId);
      setSelectedSymptoms(registrationData.motherSymptomsIds || []);
    }
  }, []);

  useEffect(() => {
    // Update isOtherSelected when symptoms or selectedSymptoms change
    if (symptoms.length > 0 && selectedSymptoms.length > 0) {
      const sortedSymptoms = [...symptoms].sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
      const lastSymptom = sortedSymptoms[sortedSymptoms.length - 1];
      setIsOtherSelected(selectedSymptoms.includes(lastSymptom?.id));
    } else {
      setIsOtherSelected(false);
    }
  }, [symptoms, selectedSymptoms]);



  useEffect(() => {
    props.setRegistrationData({motherSymptomsIds: selectedSymptoms});
  }, [selectedSymptoms]);

  const handleMotherSymptomChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {value} = e.target;
    setMotherSymptom(value);
    props.setRegistrationData({patientCase: {...patientCase, motherSymptom: value}});
  };

  const handleSymptomChange = event => {
    // Sort symptoms by displayOrder and check if the last symptom (highest displayOrder) is selected
    const sortedSymptoms = [...symptoms].sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
    const lastSymptom = sortedSymptoms[sortedSymptoms.length - 1];
    setIsOtherSelected(selectedSymptoms.includes(lastSymptom?.id));
  };

  return (
    <div>
      <h2 className="mb-4">Does the mother show any signs of the following symptoms?</h2>
      <h5 className="text-primary mb-4 text-uppercase">{patientCase?.emergencyType}</h5>
      <AvForm onValidSubmit={patientCase.type === CaseType.BOTH ? props.handleNextStep : props.onFinish}>
        <Row>
          <Col md="12">
            <AvGroup>
              <AvCheckboxGroup
                name="motherSymptoms"
                className="form-control"
                style={{overflowY: 'auto', height: '400px'}}
                value={selectedSymptoms}
              >
                {loading ? (
                  <p>Loading symptoms...</p>
                ) : (
                  [...symptoms]
                    .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
                    .map(symptom => (
                      <AvCheckbox
                        key={symptom.id}
                        value={symptom.id}
                        label={symptom.enCondition}
                        onChange={handleSymptomChange}
                      />
                    ))
                )}
              </AvCheckboxGroup>
            </AvGroup>
          </Col>
        </Row>
        {isOtherSelected && (
          <Row>
            <Col md="12">
              <AvGroup>
                <Label for="motherSymptom">Clinical Details</Label>
                <AvInput
                  type="textarea"
                  name="motherSymptom"
                  id="motherSymptom"
                  value={motherSymptom}
                  onChange={handleMotherSymptomChange}
                  rows={5}
                  required
                />
              </AvGroup>
            </Col>
          </Row>
        )}
        <RegistrationButtons
          currentStep={props.currentStep}
          handlePreviousStep={props.handlePreviousStep}
          updating={props.updating}
          isLastStep={patientCase.type !== CaseType.BOTH}
        />
      </AvForm>
    </div>
  );
};

const mapStateToProps = (state: IRootState) => ({
  countryId: state.registration.entity.countryId,
  symptoms: state.symptom.entities,
  loading: state.symptom.loading,
  patientCase: state.registration.entity.patientCase,
  registrationData: state.registration.entity,
});

const mapDispatchToProps = {
  setRegistrationData,
  getSymptomsByEmergency,
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;

export default connect(mapStateToProps, mapDispatchToProps)(MotherSymptoms);
